import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";

interface FormValues {
  date: string;
  mood: string;
  energy_level?: string;
  sleep_quality?: string;
  sleep_duration?: string;
  physical_activity?: string;
  water_intake?: string;
  appetite?: string;
  stress_level?: string;
  symptoms: string;
  medication_adherence?: string;
  notes: string;
}

interface JournalFormFieldsProps {
  form: UseFormReturn<FormValues>;
}

export const JournalFormFields = ({ form }: JournalFormFieldsProps) => {
  const moodOptions = [
    { label: "Excellent", value: "EXCELLENT" },
    { label: "Bien", value: "GOOD" },
    { label: "Neutre", value: "NEUTRAL" },
    { label: "Fatigué", value: "TIRED" },
    { label: "Stressé", value: "STRESSED" },
    { label: "Anxieux", value: "ANXIOUS" },
  ];

  const energyLevels = [
    "Très énergique", "Énergique", "Modéré", "Fatigué", "Très fatigué"
  ];

  const sleepQuality = [
    "Excellent", "Bon", "Moyen", "Mauvais", "Très mauvais"
  ];

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="date"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date</FormLabel>
            <FormControl>
              <Input required type="date" {...field} className="w-full" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Humeur</FormLabel>
            <FormControl>
              <select
                {...field}
                required
                className="w-full p-2 border rounded-md bg-background"
              >
                <option value="">Sélectionnez votre humeur</option>
                {moodOptions.map((mood) => (
                  <option key={mood.value} value={mood.value}>
                    {mood.label}
                  </option>
                ))}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="energy_level"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Niveau d'énergie</FormLabel>
            <FormControl>
              <select
                {...field}
                className="w-full p-2 border rounded-md bg-background"
              >
                <option value="">Sélectionnez votre niveau d'énergie</option>
                {energyLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="sleep_quality"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Qualité du sommeil</FormLabel>
              <FormControl>
                <select
                  {...field}
                  className="w-full p-2 border rounded-md bg-background"
                >
                  <option value="">Sélectionnez la qualité</option>
                  {sleepQuality.map((quality) => (
                    <option key={quality} value={quality}>
                      {quality}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="sleep_duration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Durée du sommeil (heures)</FormLabel>
              <FormControl>
                <Input type="number" min="0" max="24" step="0.5" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="physical_activity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Activité physique</FormLabel>
            <FormControl>
              <Input placeholder="Ex: 30 min de marche" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="water_intake"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Consommation d'eau (verres)</FormLabel>
              <FormControl>
                <Input type="number" min="0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="appetite"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Appétit</FormLabel>
              <FormControl>
                <select
                  {...field}
                  className="w-full p-2 border rounded-md bg-background"
                >
                  <option value="">Sélectionnez</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Normal">Normal</option>
                  <option value="Faible">Faible</option>
                  <option value="Très faible">Très faible</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="stress_level"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Niveau de stress (1-10)</FormLabel>
            <FormControl>
              <Input type="number" min="1" max="10" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="symptoms"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Symptômes</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Entrez vos symptômes, séparés par des virgules" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="medication_adherence"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Prise des médicaments</FormLabel>
            <FormControl>
              <select
                {...field}
                className="w-full p-2 border rounded-md bg-background"
              >
                <option value="">Sélectionnez</option>
                <option value="Tous pris">Tous pris comme prescrit</option>
                <option value="Partiellement">Partiellement pris</option>
                <option value="Oubliés">Oubliés</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notes additionnelles</FormLabel>
            <FormControl>
              <Textarea 
                {...field} 
                placeholder="Ajoutez des notes supplémentaires"
                className="min-h-[100px]"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};